import React, { useId } from 'react';
import { twJoin } from 'tailwind-merge';
import { useCurrencyFormatter } from '@/ui/hooks/useCurrencyFormatter';
import OptimizedImage from '@/ui/components/shared/OptimizedImage';
import { useTranslations } from 'next-intl';
import { Bundle, ChooserDesignVariants } from '@/ui/services/BundleService';
import { RichTextRenderer } from '@/ui/components/tileRenderers/RichTextRenderer';
import { BundleConfigurator } from '@/ui/components/bundle-configurator/bundle-configurator';
import { ProductBadge } from '@/ui/components/product-badge/product-badge';
import { CLUB_EXCLUSIVE_BADGE_KEY } from '@/models/product-badge-model';
import { useBundleContext } from '@/ui/providers/bundleProvider';
import { useProductRuleService } from '@/ui/providers/product-rules-provider';
import { getOptionTileStateClasses } from '@/ui/components/shared/option-tile/option-tile-styles';
import { BundleHostSlot } from '@/ui/components/pages/ProductDetail/types/bundle-host-slot';
import { SelectedCheckBadge } from '@/ui/components/pages/ProductDetail/selected-check-badge/selected-check-badge';
import { EmptyCheckBadge } from '@/ui/components/pages/ProductDetail/empty-check-badge/empty-check-badge';

export interface BundleOptionProps {
  bundle: Bundle;
  isSubscription?: boolean;
  hostSlot?: BundleHostSlot;
}

// Placed in the flow beside the title; mt-1 centres the 20 px circle on the title line.
const INDICATOR_CLASS_NAME = 'static mr-3 mt-1 shrink-0';

export function BundleOption({ bundle, isSubscription, hostSlot }: Readonly<BundleOptionProps>) {
  const t = useTranslations('BundleOption');

  const {
    selectedBundle,
    selectedVariants,
    handleSelectVariant,
    updateSelectedVariantsForBundle,
    selectBundle,
    hostProductId,
  } = useBundleContext();
  const isSelected = selectedBundle?.documentExtRef === bundle.documentExtRef;
  const priceSelection = isSelected ? selectedVariants : undefined;
  const isExpanded =
    isSelected &&
    !isSubscription &&
    !bundle.hasSubscription &&
    !bundle.isUpgradeBundle &&
    bundle.hasSelectableSlots();
  const expansionId = useId();
  const clubBadge = useProductRuleService()
    .getBundleBadges(bundle.getConfiguration(priceSelection))
    .find(({ key }) => key === CLUB_EXCLUSIVE_BADGE_KEY);

  const { formatCurrency, formatSubscriptionPrice } = useCurrencyFormatter();

  const toggleSelected = () => {
    if (isSubscription) {
      return;
    }
    if (isSelected) {
      selectBundle(undefined);
    } else {
      selectBundle(bundle);
      updateSelectedVariantsForBundle(bundle);
    }
  };

  const renderSubscriptionPrice = () => (
    <p className="mb-1 whitespace-nowrap text-lg font-bold">
      {formatSubscriptionPrice(bundle.subscription?.recurringPeriod)}
    </p>
  );

  const renderFirstProductPrice = () => {
    const firstBundleProduct = bundle.products[0];
    return firstBundleProduct.isFree ? (
      <p className="mb-1 text-lg font-bold text-green">{t('Free')}</p>
    ) : (
      <p className="mb-1 text-lg font-bold">{formatCurrency(firstBundleProduct.priceVariant)}</p>
    );
  };

  const renderRegularPrice = () => {
    const saving = bundle.getSaving(priceSelection);

    return (
      <>
        <p className="mb-1 text-lg font-bold">
          {formatCurrency(bundle.getFinalPrice(priceSelection))}
        </p>
        {saving !== 0 && (
          <>
            <p className="text-sm text-red line-through">
              {formatCurrency(bundle.getTotalOriginalPrice(priceSelection))}
            </p>
            <p className="text-sm font-semibold text-green">
              {t('Save')} {formatCurrency(saving)}
            </p>
          </>
        )}
      </>
    );
  };

  const renderPrice = () => {
    if (isSubscription && bundle.subscription?.recurringPeriod) {
      return renderSubscriptionPrice();
    }
    if (bundle.chooserDeisgnVariant === ChooserDesignVariants.firstProduct) {
      return renderFirstProductPrice();
    }
    return renderRegularPrice();
  };

  return (
    <div
      data-testid="bundle-option"
      className={twJoin(
        'rounded-xl',
        getOptionTileStateClasses(isSelected),
        !isSelected && 'bg-white',
      )}
    >
      <button
        onClick={toggleSelected}
        type="button"
        aria-pressed={isSelected}
        aria-controls={isExpanded ? expansionId : undefined}
        className="flex w-full cursor-pointer justify-between p-3 text-start"
      >
        <div className="flex">
          {!isSubscription &&
            (isSelected ? (
              <SelectedCheckBadge className={INDICATOR_CLASS_NAME} />
            ) : (
              <EmptyCheckBadge className={INDICATOR_CLASS_NAME} />
            ))}
          {isSubscription ||
            (bundle.chooserDeisgnVariant === ChooserDesignVariants.firstProduct && (
              <div className="relative mr-4 h-16 w-16 flex-shrink-0 overflow-hidden rounded-md border border-neutral-10">
                <OptimizedImage
                  image={bundle.products[0].defaultVariant?.images[0]}
                  fill
                  sizes="100px"
                  className="object-contain py-2"
                />
              </div>
            ))}
          <div className="mr-2">
            <p className="mb-1 text-lg font-bold">
              {bundle.chooserDeisgnVariant === ChooserDesignVariants.firstProduct
                ? bundle.products[0].defaultVariant.name
                : bundle.name}
            </p>
            {/* The expansion lists the bundle's items, so the summary line is left out. */}
            {!isExpanded && (
              <div className="text-neutral-60">
                {bundle.description ? (
                  <RichTextRenderer blocks={bundle.description} />
                ) : (
                  bundle.getProductNames(' + ')
                )}
              </div>
            )}
            {/* Below md the chooser hides the gallery and the badges on it. */}
            {clubBadge && (
              <ProductBadge
                label={clubBadge.label}
                color={clubBadge.color}
                icon={clubBadge.icon}
                className="mt-2 md:hidden"
              />
            )}
          </div>
        </div>
        <div className="flex flex-col justify-between">{renderPrice()}</div>
      </button>
      {isExpanded && (
        <div
          id={expansionId}
          data-testid="bundle-option-expansion"
          className="mx-3 border-t border-dashed border-neutral-50 py-3"
        >
          <BundleConfigurator
            bundle={bundle}
            selectedVariants={selectedVariants}
            onSelectVariant={handleSelectVariant}
            hostSlot={hostSlot}
            hostProductId={hostProductId}
          />
        </div>
      )}
    </div>
  );
}
